/**
 * PIV2: Badges — the Top Rated child block (blocks/_piv2-badge-top-rated.liquid).
 *
 * The PowerReviews average only exists once ui.js mounts the review snippet
 * into #pr-reviewsnippet (snippets/powerreviews.liquid), so Liquid renders an
 * empty holder. Once `.pr-snippet-rating-decimal` appears, every Top Rated
 * holder in the same product info column is filled when the rating is
 * >= its data-min. The holder sits wherever the merchant placed the block, so
 * no ordering logic is needed, and the sibling swap never touches it.
 */
(() => {
  if (window.__piv2BadgesInit) return;
  window.__piv2BadgesInit = true;

  const HOLDER_SELECTOR = '[data-piv2-badge="top-rated"]';
  const RATING_SELECTOR = '#pr-reviewsnippet .pr-snippet-rating-decimal';
  const SCOPE_SELECTOR = '.product-information-v2__info, .product-information-v2__details';
  const DEFAULT_MIN_RATING = 4;

  function fillTopRated(holder) {
    if (holder.querySelector('.badge')) return;
    const api = globalThis.Piv2SiblingProduct;
    if (!api || typeof api.buildTopRatedSpan !== 'function') return;
    holder.innerHTML = api.buildTopRatedSpan(holder.dataset.label);
  }

  /** Last rating read from PowerReviews; reused after editor re-renders. */
  let lastRating = null;

  function fillHolders(scope, rating) {
    for (const holder of scope.querySelectorAll(HOLDER_SELECTOR)) {
      const min = parseFloat(holder.dataset.min);
      if (rating >= (Number.isFinite(min) ? min : DEFAULT_MIN_RATING)) fillTopRated(holder);
    }
  }

  /** @returns {boolean} true once a rating was read (badge shown or not). */
  function applyRating(decimalEl) {
    const rating = parseFloat((decimalEl.textContent || '').trim());
    if (!Number.isFinite(rating)) return false;

    lastRating = rating;
    fillHolders(decimalEl.closest(SCOPE_SELECTOR) || document, rating);
    return true;
  }

  // Theme editor: moving or editing a badge block re-renders the section with
  // an empty Top Rated holder, and this script (guarded above) never re-runs.
  // The PowerReviews snippet lives in the same section, so it comes back
  // empty too and is never re-mounted (powerreviews.liquid only loads on
  // DOMContentLoaded) — re-apply the remembered rating instead.
  if (window.Shopify?.designMode) {
    const reapply = () => {
      const decimalEl = document.querySelector(RATING_SELECTOR);
      if (decimalEl && applyRating(decimalEl)) return;
      if (lastRating !== null) fillHolders(document, lastRating);
    };
    document.addEventListener('shopify:section:load', reapply);
    document.addEventListener('shopify:block:select', reapply);
  }

  function init() {
    if (!document.querySelector(HOLDER_SELECTOR)) return;

    const mounted = document.querySelector(RATING_SELECTOR);
    if (mounted && applyRating(mounted)) return;

    // PowerReviews mounts late (and positionReviewSnippet moves the id across
    // breakpoints). Products without reviews never render a decimal, so the
    // observer simply stays idle for them.
    const observer = new MutationObserver(() => {
      const decimalEl = document.querySelector(RATING_SELECTOR);
      if (decimalEl && applyRating(decimalEl)) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
